/* eslint-disable no-unused-vars */

import {
  FORM_COMPONENT_BASE_STYLES,
  type FormComponentSize,
  getFormComponentSizeClasses,
} from '@ui/lib/form-sizing';
import { cn } from '@ui/lib/utils';
import { Minus, Plus } from 'lucide-react';
import * as React from 'react';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  showNumberToggle?: boolean;
  onNumberChange?: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  inputSize?: FormComponentSize;
  toggleColors?: {
    bg?: string;
    bgHover?: string;
    bgFocus?: string;
    icon?: string;
    iconHover?: string;
    iconFocus?: string;
  };
}
/* eslint-enable no-unused-vars */

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      inputSize = 'md',
      showNumberToggle,
      onNumberChange,
      minValue = 1,
      maxValue = 999,
      toggleColors = {
        bg: 'bg-slate-50',
        bgHover: 'hover:bg-slate-50/80 transition duration-200 ease-in-out',
        bgFocus: 'focus:bg-slate-100',
        icon: 'text-slate-800',
        iconHover: 'group-hover:text-slate-800',
        iconFocus: 'group-focus:text-slate-800',
      },
      onChange,
      value: propValue,
      ...rest
    },
    ref
  ) => {
    const currentValue: string | number | readonly string[] | undefined =
      propValue !== undefined && propValue !== null ? propValue : '';

    const sizeClasses = getFormComponentSizeClasses(inputSize);

    const handleDecrement = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        const numericValue = Number(currentValue) || minValue;
        if (numericValue > minValue && onNumberChange) {
          onNumberChange(numericValue - 1);
        }
      },
      [currentValue, minValue, onNumberChange]
    );

    const handleIncrement = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        const numericValue = Number(currentValue) || minValue;
        if (numericValue < maxValue && onNumberChange) {
          onNumberChange(numericValue + 1);
        }
      },
      [currentValue, minValue, maxValue, onNumberChange]
    );

    const handleInputChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = parseInt(event.target.value, 10);
        if (!isNaN(newValue) && newValue >= minValue && newValue <= maxValue) {
          onNumberChange?.(newValue);
          onChange?.(event);
        }
      },
      [minValue, maxValue, onNumberChange, onChange]
    );

    if (type === 'number' && showNumberToggle) {
      const numericValue = Number(currentValue) || minValue;

      const buttonClasses = cn(
        'group relative border-slate-200 focus:ring-slate-100 focus:ring-2 focus:outline-none cursor-pointer',
        'transition-all duration-240 ease-in-out',
        sizeClasses.buttonHeight,
        sizeClasses.buttonPadding,
        toggleColors.bg,
        toggleColors.bgHover,
        toggleColors.bgFocus
      );

      const iconClasses = cn(
        'transition-all duration-240 ease-in-out',
        inputSize === 'xs' ? 'w-3 h-3' : 'w-4 h-4',
        toggleColors.icon,
        toggleColors.iconHover,
        toggleColors.iconFocus
      );

      return (
        <div
          className={cn(
            'relative flex items-center',
            FORM_COMPONENT_BASE_STYLES.margin
          )}
        >
          <button
            type="button"
            onClick={handleDecrement}
            className={cn(buttonClasses, 'rounded-s-lg')}
          >
            <Minus className={iconClasses} />
          </button>
          <input
            type="number"
            value={numericValue}
            onChange={handleInputChange}
            className={cn(
              'relative z-10 flex rounded-none border border-input bg-background ring-offset-background file:border-0 file:bg-transparent file:font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-center border-x-0',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              'focus:z-20 focus-visible:rounded-lg',
              'font-bold',
              'transition-all duration-240 ease-in-out',
              FORM_COMPONENT_BASE_STYLES.placeholder,
              FORM_COMPONENT_BASE_STYLES.text,
              sizeClasses.height,
              sizeClasses.inputPadding,
              sizeClasses.text,
              className
            )}
            ref={ref}
            min={minValue}
            max={maxValue}
            {...rest}
          />
          <button
            type="button"
            onClick={handleIncrement}
            className={cn(buttonClasses, 'rounded-e-lg')}
          >
            <Plus className={iconClasses} />
          </button>
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(
          'flex w-full ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'shadow-xs hover:bg-slate-50/80 transition duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 focus:ring-transparent focus:ring-opacity-0',
          FORM_COMPONENT_BASE_STYLES.border,
          FORM_COMPONENT_BASE_STYLES.placeholder,
          FORM_COMPONENT_BASE_STYLES.text,
          FORM_COMPONENT_BASE_STYLES.margin,
          FORM_COMPONENT_BASE_STYLES.transition,
          FORM_COMPONENT_BASE_STYLES.disabled,
          sizeClasses.height,
          sizeClasses.inputPadding,
          sizeClasses.text,
          className
        )}
        ref={ref}
        onChange={onChange}
        value={currentValue}
        {...rest}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
